/**
 * Monkey Worlds — device-local star map ("w:s" → 1-3). Stars are cosmetic + advisory by spec
 * (specs/monkey-worlds/spec.md, Non-Goals): the server persists unlock progress, stars live only
 * on this device and never affect coins.
 *
 * Mirrors `warmupIntroSeenStorage.helper.ts`: web uses localStorage, native AsyncStorage, every
 * operation swallows errors (fail open → no stars shown) so storage never blocks play. Corrupt
 * JSON reads as an empty map. Intentionally NOT keyed by userId — playable anonymously, per-device.
 */

import { Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { logger } from '../../helpers/logger.helper';

const STORAGE_KEY = 'zporti-monkey-worlds-stars';

export type MonkeyWorldsStarMap = Record<string, 1 | 2 | 3>;

function parseStarMap(raw: string | null): MonkeyWorldsStarMap {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const map: MonkeyWorldsStarMap = {};
    for (const [key, value] of Object.entries(parsed)) {
      if (/^\d+:\d+$/.test(key) && (value === 1 || value === 2 || value === 3)) map[key] = value;
    }
    return map;
  } catch {
    return {};
  }
}

export async function readMonkeyWorldsStars(): Promise<MonkeyWorldsStarMap> {
  try {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      return parseStarMap(localStorage.getItem(STORAGE_KEY));
    }
    return parseStarMap(await AsyncStorage.getItem(STORAGE_KEY));
  } catch (error) {
    if (__DEV__) logger.warn('monkeyWorldsStarsStorage: read failed', { error });
    return {};
  }
}

/** Persist a clear's stars — merges by keeping the best result per level. */
export async function saveMonkeyWorldsStars(
  world: number,
  slot: number,
  stars: 1 | 2 | 3,
): Promise<MonkeyWorldsStarMap> {
  const map = await readMonkeyWorldsStars();
  const key = `${world}:${slot}`;
  if ((map[key] ?? 0) >= stars) return map;
  map[key] = stars;
  try {
    const raw = JSON.stringify(map);
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, raw);
    } else {
      await AsyncStorage.setItem(STORAGE_KEY, raw);
    }
  } catch (error) {
    if (__DEV__) logger.warn('monkeyWorldsStarsStorage: write failed', { error });
  }
  return map;
}
